import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Component, effect, inject, NgZone, OnInit } from '@angular/core';
import { AuthService } from '@auth0/auth0-angular';
import { App } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import { Keyboard } from '@capacitor/keyboard';
import { SplashScreen } from '@capacitor/splash-screen';
import { IonApp, IonRouterOutlet, MenuController, Platform } from '@ionic/angular/standalone';
import { Subject, switchMap, take, takeUntil, of, timeout, catchError } from 'rxjs';
import { environment } from '../environments/environment';
import { ClientSupportLoginComponent } from './components/menus/client-support-login/client-support-login.component';
import { ErrorResponse } from './models/error-response';
import { AppUpdateService } from './services/app-update-service/app-update.service';
import { AuthenticationService } from './services/authentication-service/authentication.service';
import { ErrorService } from './services/error-service/error.service';
import { HttpHelperService } from './services/http-helper/http-helper.service';
import { IdleService } from './services/idle-service/idle-service.service';
import { PageHelperService } from './services/page-helper/page-helper.service';
import { SignalsService } from './services/signals-service/signals.service';
import { Auth0Service } from './services/auth0-service/auth0.service';
import { Router } from '@angular/router';

declare global {
  interface Window {
    [key: string]: {
      hier1: any;
      pageName: string;
      clearVars: any;
      track: any;
      getVar: any;
      stateValue: string;
      quoteZipValue: string;
      enterprise: any;
      execute: any;
      vendorName: string;
      externalURL: string;
      clickItem: string;
      formName: string;
      quoteType: string;
      icid: string;
      clientId: string;
      clientEmail: string;
      Account: any; // eGain
      push: any; // eGain
      claimType: any;
    };
  }
}

declare var dT_;

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  imports: [IonApp, IonRouterOutlet, ClientSupportLoginComponent],
})
export class AppComponent implements OnInit {
  auth = inject(AuthService);
  private authenticationService = inject(AuthenticationService);
  private ngZone = inject(NgZone);
  private http = inject(HttpClient);
  private pageHelper = inject(PageHelperService);
  private platform = inject(Platform);
  private errorService = inject(ErrorService);
  httpHelper = inject(HttpHelperService);
  private menuCtrl = inject(MenuController);
  private appUpdateService = inject(AppUpdateService);
  private signals = inject(SignalsService);
  private idleService = inject(IdleService);
  private auth0Service = inject(Auth0Service);
  private router = inject(Router);

  private ngUnsubscribe = new Subject<void>();
  private callbackUri;

  constructor() {
    const devEnvString = localStorage.getItem('devEnvironmentObj');
    let authConfig = Capacitor.isNativePlatform() ? environment.auth0_mobile : environment.auth0;

    if (devEnvString) {
      try {
        const devEnv = JSON.parse(devEnvString);
        if (devEnv?.auth0_mobile) {
          authConfig = devEnv.auth0_mobile;
        }
      } catch (e) {
        console.error('Invalid devEnvironmentObj in localStorage', e);
      }
    }
    this.callbackUri = authConfig.callBackUrl;

    effect(() => {
      if (this.signals.appInfo()) {
        this.appUpdateService.checkForForceUpdate();
      }
      if (this.signals.appInfo() !== null) {
        this.idleService.watch();
      }
    });

    if (typeof dT_ !== 'undefined' && dT_.initAngularNg) {
      dT_.initAngularNg(this.http, HttpHeaders);
    }

    this.initializeApp();
    this.initializeErrors();
    this.menuCtrl.enable(true);
  }

  presentingElement = document.querySelector('.ion-page');

  initializeApp(): void {
    this.platform.ready().then(() => {
      this.setupNativeFeatures();
      this.listenForAppResume();
    });
  }

  private setupNativeFeatures(): void {
    SplashScreen.hide();

    if (Capacitor.isPluginAvailable('Keyboard')) {
      Keyboard.setAccessoryBarVisible({ isVisible: true });
    }
  }

  private listenForAppResume(): void {
    this.platform.resume.subscribe(() => {
      this.httpHelper
        .appInfoReady()
        .pipe(take(1))
        .subscribe(() => {
          this.appUpdateService.checkForForceUpdate();
        });
    });
  }

  async ngOnInit(): Promise<void> {
    if (Capacitor.isNativePlatform()) {
      this.initMobileAuthListener();
    } else {
      this.handleWebAuth();
    }
  }

  private initMobileAuthListener(): void {
    App.addListener('appUrlOpen', ({ url }) => {
      this.ngZone.run(async () => {
        await Browser.close();

        if (!url.includes(this.callbackUri)) return;

        const isCallback = url.includes('/callback');
        const hasCode = url.includes('code=');
        const hasError = url.includes('error=');
        const isLogout = url.includes('/callback/logout');

        if (isLogout) {
          this.router.navigate(['/login'], { replaceUrl: true }).then(() => {
            window.location.reload();
          });
        }

        if (isCallback && hasCode) {
          this.handleTokenExchange(url);
          return;
        }

        if (isCallback && hasError) {
          this.handleRedirectError(url);
          return;
        }
      });
    });
  }

  private handleWebAuth(): void {
    const url = window.location.href;
    const origin = window.location.origin;

    const isCallback = url.includes('/callback');
    const hasCode = url.includes('code=');
    const hasError = url.includes('error=');

    if (isCallback && hasCode) {
      this.handleTokenExchange(url);
      return;
    }

    if (isCallback && hasError) {
      this.handleRedirectError(url);
      return;
    }

    if (this.shouldCheckSession(url, origin)) {
      this.checkExistingSession();
    }
  }

  private async handleTokenExchange(url: string): Promise<void> {
    await this.pageHelper.showLoader();

    this.httpHelper
      .appInfoReady()
      .pipe(take(1))
      .subscribe(() => {
        this.authenticationService.exchangeCodeForToken(url);
      });
  }

  private async handleRedirectError(url: string): Promise<void> {
    this.httpHelper
      .appInfoReady()
      .pipe(take(1))
      .subscribe(() => {
        this.authenticationService.handleRedirectCallback(url);
      });
  }

  private shouldCheckSession(url: string, origin: string): boolean {
    return (
      (!url.includes('/callback?code=') &&
        !url.includes('/callback?error=') &&
        url.includes('/login')) ||
      url.includes('portal/dispatcher/sign-in') ||
      url.includes('portal/dispatcher/my-account') ||
      url.includes('portal/dispatcher/forgot-password') ||
      (url.includes('/registration') && !url.includes('registration/assisted-registration')) ||
      url.includes('/?iss=') || //redirect from change password flowI'm still not y
      url === origin + '/'
    );
  }

  private async checkExistingSession(): Promise<void> {
    this.pageHelper.showLoader().then(async () => {
      this.httpHelper.fetchAppInfo().subscribe(async (data) => {
        this.authenticationService
          .isAccessTokenValid()
          .pipe(
            take(1),
            timeout(20000), // 20 seconds timeout
            catchError(() => {
              this.pageHelper.closeLoader();
              this.auth0Service.logout();
              return of(false);
            }),
          )
          .subscribe(async (isAuthenticated) => {
            if (isAuthenticated) {
              try {
                await this.authenticationService.loginWithTokens();
              } catch (error) {
                //close loader and logout if loginWithTokens fails
                this.pageHelper.closeLoader();
                this.auth0Service.logout();
              }
            } else {
              this.pageHelper.closeLoader();
            }
          });
      });
    });
  }

  private initializeErrors() {
    this.errorService
      .getErrors()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(async (error: ErrorResponse) => {
        await this.pageHelper.showAlert({
          header: error.title ? error.title : 'Error',
          message: error.message,
          buttons: ['OK'],
        });
      });
  }
}
